import { useEffect, useState } from "react";
import { getListTournamentEvents, getCurrentSeason, getGeneralRanking } from "../../api";
import { TournamentEventCard } from "../../components/common/TournamentEventCard";
import { RankingTable } from "../../components/common/RankingTable";
import { Season, TournamentEvent, GeneralRanking, StateToInt } from "../../types";

function compareEventsToPlay(a: TournamentEvent, b: TournamentEvent) {
  // Prima ordino per stato decrescente
  let diff = StateToInt[b.state] - StateToInt[a.state];
  // se stati uguali, ordino per data crescente
  if (diff === 0) {
    diff = (a.date || "").localeCompare(b.date || "");
  }
  return diff;
}

function compareEventsPlayed(a: TournamentEvent, b: TournamentEvent) {
  // Prima ordino per stato crescente
  let diff = StateToInt[b.state] - StateToInt[a.state];
  // se stati uguali, ordino per data decrescente
  if (diff === 0) {
    diff = (b.date || "").localeCompare(a.date || "");
  }
  return diff;
}

export const HomePage: React.FC = () => {
  const [season, setSeason] = useState<Season | null>(null);
  const [nextEvents, setNextEvents] = useState<TournamentEvent[]>([]);
  const [pastEvents, setPastEvents] = useState<TournamentEvent[]>([]);
  const [ranking, setRanking] = useState<GeneralRanking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. stagione corrente (localStorage, fallback alla default da backend)
        const currentSeason = await getCurrentSeason();
        setSeason(currentSeason);

        // 2. carico ranking attuale
        const rankingData = await getGeneralRanking(currentSeason.id);
        setRanking(rankingData.slice(0, 5)); // top 5

        // 3. carico eventi della stagione
        const events = await getListTournamentEvents({ seasonId: currentSeason.id });

        const upcoming = events
          .filter((e: TournamentEvent) => StateToInt[e.state] <= StateToInt["confermato"])
          .sort((a, b) => compareEventsToPlay(a, b))
          .slice(0, 3);

        const concluded = events
          .filter((e: TournamentEvent) => StateToInt[e.state] >= StateToInt["in_corso"])
          .sort((a, b) => compareEventsPlayed(a, b)) 
          .slice(0, 3);

        setNextEvents(upcoming);
        setPastEvents(concluded);
      } catch (err) {
        console.error("Errore nel caricamento homepage", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="p-4">Caricamento...</div>;
  }

  return (
    <div className="container mx-auto space-y-8">
      {/* Titolo stagione */}
      {season && (
        <h1 className="text-2xl font-bold text-left">
          {season.name}
        </h1>
      )}

      {/* Sezione Prossimi Tornei */}
      {nextEvents.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold mb-4">Prossimi Tornei</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {nextEvents.map((event) => (
                <TournamentEventCard key={event.id} tournamentEvent={event} />
              ))
            }
          </div>
        </section>
      )}

      {/* Sezione Classifica Attuale */}
      {ranking.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold mb-4">Classifica Attuale</h2>
          <RankingTable type="general" data={ranking} />
        </section>
      )}
      

      {/* Sezione Tornei Conclusi */}
      {pastEvents.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold mb-4">Tornei Conclusi</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastEvents.map((event) => (
              <TournamentEventCard key={event.id} tournamentEvent={event} />
            ))
          }
          </div>
        </section>
      )}

      {nextEvents.length === 0 && pastEvents.length === 0 && ranking.length === 0 && (
        <div className="text-center text-gray-500">
          <p>Non ci sono eventi disponibili al momento.</p>
        </div>
      )}
    </div>
  );
}
